import { useState, useMemo, useEffect } from "react";

import type { FieldId } from "src/types";
import {
  AiChecklistSuggestion,
  AiLinkedFieldSuggestionValue,
  AiSuggestionsMap,
  AiSuggestionSelectionMap
} from "src/ts-types";
import { isAiLinkedFieldSuggestionValue } from "src/utils/ai";

const linkedFieldSelectAll = (
  linkedFieldSuggestion: AiLinkedFieldSuggestionValue["embeddedFields"],
  deselect: boolean = false
): Set<number> => {
  if (deselect) {
    return new Set();
  }
  const embeddedFieldsIndices = linkedFieldSuggestion.length
    ? new Set([...Array(linkedFieldSuggestion.length).keys()])
    : new Set<never>();
  return embeddedFieldsIndices;
};

const fieldToggleAll = (
  fieldId: FieldId,
  checklistFieldSuggestionsMap: AiSuggestionsMap,
  selectedSuggestions: AiSuggestionSelectionMap
): AiSuggestionSelectionMap => {
  const newSelectedSuggestions = new Map(selectedSuggestions);
  if (
    newSelectedSuggestions.has(fieldId) &&
    newSelectedSuggestions.get(fieldId)?.length
  ) {
    newSelectedSuggestions.delete(fieldId);
    return newSelectedSuggestions;
  }
  let newMultiSuggestions = Array.from(selectedSuggestions.get(fieldId) ?? []);
  const suggestion = checklistFieldSuggestionsMap.get(fieldId);
  if (!suggestion) return newSelectedSuggestions;
  if (Array.isArray(suggestion.value)) {
    suggestion.value.forEach((v, i) => {
      if (
        suggestion.type === "link" &&
        !Array.isArray(v) &&
        typeof v === "object"
      ) {
        const allEmbeddedFields = linkedFieldSelectAll(v.embeddedFields);
        newMultiSuggestions[i] = allEmbeddedFields;
      } else {
        newMultiSuggestions[i] = new Set();
      }
    });
  } else {
    newMultiSuggestions[0] = new Set();
  }
  newSelectedSuggestions.set(fieldId, newMultiSuggestions);
  return newSelectedSuggestions;
};

const getAllEmbeddedFieldSuggestions = (
  fieldId: FieldId,
  fieldSuggestionIndex: number,
  checklistFieldSuggestionsMap: AiSuggestionsMap
) => {
  const fieldSuggestion = checklistFieldSuggestionsMap.get(fieldId);
  if (!fieldSuggestion || !Array.isArray(fieldSuggestion.value)) return [];
  const fieldSuggestionValue = fieldSuggestion.value[fieldSuggestionIndex];
  if (isAiLinkedFieldSuggestionValue(fieldSuggestionValue)) {
    return fieldSuggestionValue.embeddedFields;
  }
  return [];
};

/**
 * Manages selection state for AI checklist suggestions, including
 * linked-field suggestions with embedded fields. Exposes helpers to
 * toggle selections at field, suggestion, or embedded-suggestion levels
 * and to query selection state.
 *
 * `toggleSuggestion` Behavior:
 * - Call without indices to toggle select-all for a field.
 * - Provide a suggestion index to toggle that suggestion.
 * - For linked-field suggestions, provide both suggestion and embedded
 * indices to toggle a nested value.
 *
 * @param {AiChecklistSuggestion[]} [checklistFieldSuggestions=[]] Array of AI suggestions for checklist fields.
 * @param {AiSuggestionSelectionMap} [initiallySelected=new Map()] Optional preselected suggestions map.
 * @returns {{
 *   checklistFieldSuggestionsMap: AiSuggestionsMap,
 *   selectedSuggestions: AiSuggestionSelectionMap,
 *   toggleSuggestion: (fieldId: FieldId, fieldSuggestionIndex?: number, embeddedSuggestionIndex?: number) => void,
 *   isSuggestionSelected: (fieldId: FieldId, fieldSuggestionIndex: number, embeddedSuggestionIndex?: number) => boolean
 * }}
 *
 * @example
 * const { selectedSuggestions, toggleSuggestion, isSuggestionSelected } =
 *   useSelectedAiSuggestions(fields);
 *
 * // Pass into a table (see usage in SuggestionTable.tsx)
 * // <Table data={fields} onRowSelect={toggleSuggestion} selectedRows={...} />
 */
const useSelectedAiSuggestions = (
  checklistFieldSuggestions: Array<AiChecklistSuggestion> = [],
  initiallySelected: AiSuggestionSelectionMap = new Map()
) => {
  const checklistFieldSuggestionsMap = useMemo(() => {
    const map: AiSuggestionsMap = new Map();
    checklistFieldSuggestions.forEach(suggestion => {
      map.set(suggestion.id, suggestion);
    });
    return map;
  }, [checklistFieldSuggestions]);

  const [selectedSuggestions, setSelectedSuggestions] =
    useState(initiallySelected);

  useEffect(() => {
    if (selectedSuggestions.size === 0 && initiallySelected.size > 0) {
      setSelectedSuggestions(initiallySelected);
    }
  }, [initiallySelected]);

  const toggleSuggestion = (
    fieldId: FieldId,
    fieldSuggestionIndex?: number,
    embeddedSuggestionIndex?: number
  ) => {
    setSelectedSuggestions((prev): AiSuggestionSelectionMap => {
      const newSelectedSuggestions: AiSuggestionSelectionMap = new Map(prev);

      const oldFieldSuggestions = newSelectedSuggestions.get(fieldId);
      const newFieldSuggestions = oldFieldSuggestions
        ? [...oldFieldSuggestions]
        : [];
      newSelectedSuggestions.set(fieldId, newFieldSuggestions);

      if (fieldSuggestionIndex === undefined) {
        // "Select all" behavior
        return fieldToggleAll(
          fieldId,
          checklistFieldSuggestionsMap,
          newSelectedSuggestions
        );
      }
      if (embeddedSuggestionIndex !== undefined) {
        const oldEmbeddedSet =
          newFieldSuggestions[fieldSuggestionIndex] ?? new Set<number>();
        const newEmbeddedSet = new Set(oldEmbeddedSet);
        if (newEmbeddedSet.has(embeddedSuggestionIndex)) {
          newEmbeddedSet.delete(embeddedSuggestionIndex);
        } else {
          newEmbeddedSet.add(embeddedSuggestionIndex);
        }
        newFieldSuggestions[fieldSuggestionIndex] = newEmbeddedSet;
      } else {
        if (newFieldSuggestions[fieldSuggestionIndex] != null) {
          newFieldSuggestions[fieldSuggestionIndex] = null;
          if (newFieldSuggestions.every(set => set == null)) {
            newSelectedSuggestions.delete(fieldId);
          }
        } else {
          const embeddedFieldSuggestions = getAllEmbeddedFieldSuggestions(
            fieldId,
            fieldSuggestionIndex,
            checklistFieldSuggestionsMap
          );
          const newEmbeddedSet =
            embeddedFieldSuggestions && embeddedFieldSuggestions.length > 0
              ? linkedFieldSelectAll(embeddedFieldSuggestions)
              : new Set<number>();
          newFieldSuggestions[fieldSuggestionIndex] = newEmbeddedSet;
        }
      }
      return newSelectedSuggestions;
    });
  };

  const isSuggestionSelected = (
    fieldId: FieldId,
    fieldSuggestionIndex: number,
    embeddedSuggestionIndex?: number
  ) => {
    if (embeddedSuggestionIndex !== undefined) {
      const fieldSuggestions = selectedSuggestions.get(fieldId);
      if (fieldSuggestions && fieldSuggestions[fieldSuggestionIndex]) {
        return fieldSuggestions[fieldSuggestionIndex].has(
          embeddedSuggestionIndex
        );
      }
    } else {
      const fieldSuggestions = selectedSuggestions.get(fieldId);
      return fieldSuggestions?.[fieldSuggestionIndex] != null;
    }
    return false;
  };

  return {
    checklistFieldSuggestionsMap,
    selectedSuggestions,
    toggleSuggestion,
    isSuggestionSelected
  };
};

export default useSelectedAiSuggestions;
